// Pestaña Docs: el expediente del crédito en siete filas fijas, como lo pinta el mock. «Ver» pide la
// URL prefirmada del PDF a `loan-credit` y la abre en otra pestaña.
import { useState } from 'react';
import { toast } from 'sonner';
import type { LoanContractResponse } from '@fiado/type-kit/bin/loanCredit/dtos/responses/LoanContractResponse';
import { ApiError, userMessageOf } from '@/api/errors';
import { loanCreditApi } from '@/api/services/loan-credit';
import { formatTimelineDateMx } from '@/lib/dateHelpers';
import type { CreditFileBlock } from '../api/useCreditFile';
import { buildCreditDocumentRows, type CreditDocumentRow } from '../logic';
import { CreditBlock, CreditCard, CreditChip } from './CreditFileUi';

/** Lo que dice la columna derecha cuando no hay PDF que abrir. */
const STATE_LABEL = {
    unsigned: 'Sin firmar',
    'not-issued': 'No emitido',
} as const;

/** Los fallos propios de este endpoint; sin esto el operador lee el código crudo del silo. */
const DOWNLOAD_MESSAGE_BY_CODE: Record<string, string> = {
    CONTRACT_PDF_NOT_READY: 'Este documento todavía no tiene PDF generado.',
    CONTRACT_NOT_FOUND: 'Este documento ya no está en el expediente del crédito.',
    CONTRACT_PDF_URL_FAILED: 'El generador de documentos no respondió. Intenta de nuevo.',
};

function downloadErrorOf(error: unknown): string {
    if (error instanceof ApiError && DOWNLOAD_MESSAGE_BY_CODE[error.code]) {
        return DOWNLOAD_MESSAGE_BY_CODE[error.code];
    }
    return userMessageOf(error);
}

/** La hoja con la esquina doblada del mock; ni el kit ni `lucide` traen ese trazo exacto. */
function DocumentIcon() {
    return (
        <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
        >
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
        </svg>
    );
}

function DocumentRow({
    row,
    isPending,
    onOpen,
}: {
    row: CreditDocumentRow;
    isPending: boolean;
    onOpen: (row: CreditDocumentRow) => void;
}) {
    return (
        <li className="grid grid-cols-[24px_1fr_auto] items-center gap-2.5 border-b border-border/50 px-3 py-2 last:border-b-0">
            <span className="flex size-6 items-center justify-center rounded bg-info-soft text-info">
                <DocumentIcon />
            </span>
            <span className="flex min-w-0 flex-col gap-0.5">
                <CreditChip tone="neutral" className="self-start">
                    {row.chip}
                </CreditChip>
                <span className="text-[11.5px] font-semibold">{row.name}</span>
                {row.createdAt ? (
                    <span className="text-[10px] text-muted-foreground">
                        {formatTimelineDateMx(row.createdAt)}
                    </span>
                ) : null}
            </span>
            {row.state === 'available' ? (
                <button
                    type="button"
                    disabled={isPending}
                    onClick={() => {
                        onOpen(row);
                    }}
                    className="rounded-[3px] border border-border px-2 py-[3px] text-[10.5px] text-primary transition-colors hover:bg-primary/10 disabled:opacity-50"
                >
                    Ver
                </button>
            ) : (
                <span className="px-2 py-[3px] text-[10.5px] text-muted-foreground">
                    {STATE_LABEL[row.state]}
                </span>
            )}
        </li>
    );
}

export interface CreditDocsTabProps {
    creditId: string;
    contracts: CreditFileBlock<LoanContractResponse[]>;
}

export function CreditDocsTab({ creditId, contracts }: CreditDocsTabProps) {
    const rows = buildCreditDocumentRows(contracts.data ?? []);
    const [pendingRowId, setPendingRowId] = useState<string | null>(null);

    /** Pide el enlace prefirmado y lo abre. El bloqueo de ventanas se avisa, no se traga. */
    async function openDocument(row: CreditDocumentRow) {
        if (!row.contractId) return;
        setPendingRowId(row.id);
        try {
            const link = await loanCreditApi.getContractDownloadUrl(creditId, row.contractId);
            // Sin `noopener` en las features: con esa bandera `window.open` devuelve `null` aunque
            // haya abierto, y el bloqueo del navegador dejaría de distinguirse del caso bueno.
            const opened = window.open(link.url, '_blank');
            if (opened) {
                opened.opener = null;
            } else {
                toast.error('Tu navegador bloqueó la ventana', {
                    description:
                        'Permite las ventanas emergentes de este sitio e intenta de nuevo.',
                });
            }
        } catch (error) {
            toast.error('No se pudo abrir el documento', { description: downloadErrorOf(error) });
        } finally {
            // Solo apaga SU pendiente: si ya hay otra fila en vuelo, la suya no se toca.
            setPendingRowId((current) => (current === row.id ? null : current));
        }
    }

    return (
        <CreditCard
            title="Documentos del crédito"
            meta={contracts.data ? `${String(rows.length)} archivos` : undefined}
            flush
        >
            <CreditBlock
                isLoading={contracts.isLoading}
                failure={contracts.failure}
                subject="los documentos"
                onRetry={contracts.retry}
            >
                <ul>
                    {rows.map((row) => (
                        <DocumentRow
                            key={row.id}
                            row={row}
                            isPending={pendingRowId === row.id}
                            onOpen={(target) => {
                                void openDocument(target);
                            }}
                        />
                    ))}
                </ul>
            </CreditBlock>
        </CreditCard>
    );
}
